import React, { useEffect, useRef, useState } from "react";
import {
  notificationTestApi,
  createNotificationRequestId,
} from "../../utils/notificationTestApi";

const employeeLabel = (employee) =>
  `${employee.name || "Employee"} (${
    employee.employee_code || "Code unavailable"
  })`;
export default function NotificationTest() {
  const [capabilities, setCapabilities] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessRetry, setAccessRetry] = useState(0);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const request = useRef(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  useEffect(() => {
    let alive = true;
    setAccessLoading(true);
    setError("");
    notificationTestApi
      .capabilities()
      .then((data) => {
        if (alive) setCapabilities(data);
      })
      .catch((caught) => {
        if (alive) setError(caught.message);
      })
      .finally(() => {
        if (alive) setAccessLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [accessRetry]);
  useEffect(() => {
    const text = query.trim();
    if (!capabilities || !capabilities.can_send || text.length < 2) {
      setMatches([]);
      setSearching(false);
      setSearchError("");
      return undefined;
    }
    let alive = true;
    const controller = new AbortController();
    setSearching(true);
    setSearchError("");
    setMatches([]);
    const timer = setTimeout(() => {
      notificationTestApi
        .search(text, controller.signal)
        .then((data) => {
          if (alive)
            setMatches(Array.isArray(data.employees) ? data.employees : []);
        })
        .catch((caught) => {
          if (alive && caught.name !== "AbortError")
            setSearchError(caught.message);
        })
        .finally(() => {
          if (alive) setSearching(false);
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, capabilities]);
  const maximum = capabilities ? capabilities.max_recipients : 20;
  const add = (employee) => {
    setRecipients((previous) =>
      previous.some((row) => row.user_id === employee.user_id) ||
      previous.length >= maximum
        ? previous
        : [...previous, employee]
    );
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    const cleanTitle = title.trim(),
      cleanBody = message.trim();
    if (
      !cleanTitle ||
      cleanTitle.length > 200 ||
      !cleanBody ||
      cleanBody.length > 5000 ||
      !recipients.length
    ) {
      setError("Select at least one employee and enter a title and message.");
      return;
    }
    const userIds = recipients.map((row) => row.user_id).sort((a, b) => a - b);
    const fingerprint = JSON.stringify([cleanTitle, cleanBody, userIds]);
    if (!request.current || request.current.fingerprint !== fingerprint)
      request.current = { fingerprint, id: createNotificationRequestId() };
    busy.current = true;
    setSending(true);
    setError("");
    try {
      const data = await notificationTestApi.send({
        request_id: request.current.id,
        title: cleanTitle,
        body: cleanBody,
        recipient_user_ids: userIds,
      });
      if (mounted.current) {
        setResult(data);
        request.current = null;
      }
    } catch (caught) {
      if (mounted.current) setError(caught.message);
    } finally {
      busy.current = false;
      if (mounted.current) setSending(false);
    }
  };
  const refresh = async () => {
    if (!result || refreshing) return;
    setRefreshing(true);
    setError("");
    try {
      const data = await notificationTestApi.status(result.notification_id);
      if (mounted.current) setResult(data);
    } catch (caught) {
      if (mounted.current) setError(caught.message);
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  };
  return (
    <div className="wrapper wrapper-content animated fadeInRight">
      <h2>Notification test</h2>
      <p>
        Send a test message to selected employees.
      </p>
      {error && (
        <div role="alert" className="alert alert-danger">
          {error}
        </div>
      )}
      {accessLoading && <p role="status">Checking access…</p>}
      {!accessLoading && !capabilities && (
        <button
          type="button"
          className="btn btn-default"
          onClick={() => setAccessRetry((value) => value + 1)}
        >
          Try again
        </button>
      )}
      {!accessLoading && capabilities && !capabilities.can_send && (
        <div className="alert alert-info">
          {!capabilities.enabled
            ? "Web notification testing is disabled on this backend."
            : !capabilities.authorized
            ? `Your web account is not approved for notification testing. Account ID: ${capabilities.account_id}.`
            : "Notification tables or the active push_test reason are not ready on this backend."}
          <div>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setAccessRetry((value) => value + 1)}
            >
              Check again
            </button>
          </div>
        </div>
      )}
      {capabilities && capabilities.can_send && (
        <div className="ibox">
          <div className="ibox-content">
            <p>
              <strong>Environment:</strong> {capabilities.environment}
            </p>
            {!capabilities.push_enabled && (
              <div className="alert alert-warning">
                Push sending is disabled. Messages will be saved to the employee
                inbox only.
              </div>
            )}
            <form onSubmit={submit}>
              <div className="form-group">
                <label htmlFor="notification-employee-search">
                  Search employee name or employee code
                </label>
                <input
                  id="notification-employee-search"
                  className="form-control"
                  value={query}
                  maxLength={100}
                  disabled={sending}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.preventDefault();
                  }}
                  placeholder="Type at least 2 characters"
                />
                {searching && <p role="status">Searching employees…</p>}
                {searchError && (
                  <p role="alert" className="text-danger">
                    {searchError}
                  </p>
                )}
                {!searching &&
                  query.trim().length >= 2 &&
                  !searchError &&
                  !matches.length && <p>No matching employees.</p>}
                <ul
                  className="list-group"
                  aria-label="Employee search results"
                  style={{ maxHeight: 260, overflowY: "auto", marginTop: 8 }}
                >
                  {matches.map((employee) => (
                    <li key={employee.user_id} className="list-group-item">
                      <strong>{employeeLabel(employee)}</strong>
                      {employee.branch_name && (
                        <span> · {employee.branch_name}</span>
                      )}
                      <span>
                        {" "}
                        · {employee.registered_device_count} registered push
                        device(s){" "}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-default"
                        aria-label={`Select ${
                          employee.employee_code || employee.user_id
                        }`}
                        disabled={
                          sending ||
                          recipients.length >= maximum ||
                          recipients.some(
                            (row) => row.user_id === employee.user_id
                          )
                        }
                        onClick={() => add(employee)}
                      >
                        Select
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <p>
                <strong>
                  Selected employees ({recipients.length}/{maximum})
                </strong>
              </p>
              <ul className="list-group" aria-label="Selected employees">
                {recipients.map((employee) => (
                  <li className="list-group-item" key={employee.user_id}>
                    {employeeLabel(employee)}{" "}
                    <button
                      type="button"
                      className="btn btn-sm btn-default"
                      disabled={sending}
                      aria-label={`Remove ${
                        employee.employee_code || employee.user_id
                      }`}
                      onClick={() =>
                        setRecipients((previous) =>
                          previous.filter(
                            (row) => row.user_id !== employee.user_id
                          )
                        )
                      }
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
              <div className="form-group">
                <label htmlFor="notification-test-title">Title</label>
                <input
                  id="notification-test-title"
                  className="form-control"
                  required
                  maxLength={200}
                  value={title}
                  disabled={sending}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="notification-test-body">Message</label>
                <textarea
                  id="notification-test-body"
                  className="form-control"
                  required
                  rows={5}
                  maxLength={5000}
                  value={message}
                  disabled={sending}
                  onChange={(event) => setMessage(event.target.value)}
                />
              </div>
              <p>
                Burmese and English are supported. Full content is saved in the
                inbox; the phone receives a generic system alert.
              </p>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  sending ||
                  !recipients.length ||
                  !title.trim() ||
                  !message.trim()
                }
              >
                {sending ? "Saving test…" : "Send test notification"}
              </button>
            </form>
            {result && (
              <section
                aria-label="Notification test result"
                style={{ marginTop: 24 }}
              >
                <div role="status" className="alert alert-success">
                  Saved notification #{result.notification_id} to{" "}
                  {result.recipient_count} employee inbox(es).{" "}
                  {result.delivery_count} push job(s) created.
                </div>
                {!result.delivery_count && (
                  <p>
                    No push jobs were scheduled. The inbox message is still
                    available to the selected employees.
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-default"
                  disabled={refreshing}
                  onClick={refresh}
                >
                  {refreshing
                    ? "Refreshing status…"
                    : "Refresh delivery status"}
                </button>
                <div className="table-responsive" style={{ marginTop: 12 }}>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Queued</th>
                        <th>Accepted by FCM</th>
                        <th>Failed</th>
                        <th>Skipped</th>
                        <th>Error code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.recipients.map((employee) => (
                        <tr key={employee.user_id}>
                          <td>
                            {employeeLabel(employee)}
                            {!employee.delivery_count && <div>Inbox only</div>}
                          </td>
                          <td>{employee.queued}</td>
                          <td>{employee.provider_accepted}</td>
                          <td>{employee.failed}</td>
                          <td>{employee.skipped}</td>
                          <td>{employee.last_error_code || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  FCM acceptance means the provider accepted the push request;
                  it does not confirm phone display or employee reading.
                  Delivery normally begins about 10–20 seconds after saving.
                </p>
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
